import React, { useState } from 'react';
import { Dropdown, Form, InputGroup } from 'react-bootstrap';
import { Avatar } from "../../Components/ui/avatar/avatar";
import styles from './KeyDate.module.scss';
import DropdownToggle from '../DropdownToggle';

// Define user types to handle the different formats across components
export type User = {
    SubUserId?: number;
    UserID?: number;
    SubUserID?: number; // Different casing in some components
    LDDUserID?: number;
    Name?: string;
    LSUCID?: string;
};

export type SearchableUserDropdownProps = {
    users: User[];
    selectedUserId: any; // Could be string, number or 'nouser'
    currentUserId: number | string;
    onUserSelect: (userId: any) => void;
    showNotAssigned?: boolean;
    extraOptions?: Array<{id: string, label: string, icon?: string}>;
    dropdownAlign?: 'start' | 'end';
    dropdownWidth?: string;
    maxHeight?: string;
};

// Helper functions used across the components
export const getInitials = (name: string): string => {
    if (!name) return '';
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

const AVATAR_COLORS = [
    '#9E0000', '#D68400', '#408F00', '#87B45E', '#A347B6', '#5847B6', 
    '#0052AB', '#00A38C', '#006557', '#BB83AC', '#907A45', '#839ABB', 
    '#D87373', '#A45500'
];

const CURRENT_USER_COLOR = '#006A99';

export const getUserColor = (userId: number | string, currentUserId: number | string): string => {
    if (userId === currentUserId) return CURRENT_USER_COLOR;
    const numericId = Number(userId) || userId.toString().charCodeAt(0);
    return AVATAR_COLORS[Math.abs(numericId) % AVATAR_COLORS.length];
};

const SearchableUserDropdown: React.FC<SearchableUserDropdownProps> = ({
    users,
    selectedUserId,
    currentUserId,
    onUserSelect,
    showNotAssigned = false,
    extraOptions = [],
    dropdownAlign = 'end',
    dropdownWidth = '260px',
    maxHeight = '250px'
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');

    // Find the current selected user for display
    const selectedUserName = (() => {
        if (selectedUserId === 0 || selectedUserId === "all") return "All Users";
        if (selectedUserId === 'nouser') return "Not Assigned";
        if (selectedUserId === currentUserId) return "Me";
        
        return users.find(u => 
            u.SubUserId?.toString() === selectedUserId?.toString() || 
            u.SubUserID?.toString() === selectedUserId?.toString() || 
            u.UserID?.toString() === selectedUserId?.toString() ||
            u.LSUCID === selectedUserId?.toString()
        )?.Name || "All Users";
    })();

    // Filter users based on search term
    const filteredUsers = users.filter(user => 
        user.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false
    );

    // Get user ID consistently across different user structures
    const getUserId = (user: User): string => {
        return (user.SubUserId || user.SubUserID || user.UserID || user.LSUCID)?.toString() || '';
    };

    return (
        <Dropdown 
            onSelect={(eventKey) => {
                const userId = eventKey === "all" ? 0 : 
                    Number.isNaN(Number(eventKey)) ? eventKey : Number(eventKey);
                onUserSelect(userId);
                setSearchTerm('');
            }}
            align={dropdownAlign}
            style={{display:'inline-block'}}
        >
            <Dropdown.Toggle as={DropdownToggle}>
                {selectedUserName}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{padding: '0'}}>
                {users.length > 6 && (
                    <Dropdown.Header className={styles.dropdownSearchHeader}>
                        <InputGroup>
                            <InputGroup.Text style={{pointerEvents: 'none'}}>
                                <i className="fa-duotone fa-magnifying-glass"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                size="sm"
                                className="search-bar"
                            />
                        </InputGroup>
                    </Dropdown.Header>
                )}
                
                <div style={{maxHeight, width: dropdownWidth, overflow: 'auto', padding: '0 10px 10px'}}>
                    {/* Current user option (Me) */}
                    <Dropdown.Item key="current" eventKey={currentUserId.toString()}>
                        <div className="d-flex align-items-center">
                            <Avatar initials={getInitials(users.find(u => getUserId(u) === currentUserId.toString())?.Name || "CU")} size="sm" color={CURRENT_USER_COLOR} />
                            
                            <div className="text-truncate" style={{maxWidth: 'calc(100% - 30px)'}}>
                                Me
                            </div>
                        </div>
                    </Dropdown.Item>
                    
                    {/* All users option */}
                    <Dropdown.Item key="all" eventKey="all">
                        <div className="d-flex align-items-center">
                            <Avatar initials="A" size="sm" />
                            <div className="text-truncate">
                                All Users
                            </div>
                        </div>
                    </Dropdown.Item>
                    
                    {/* Not assigned option - only if needed */}
                    {showNotAssigned && (
                        <Dropdown.Item key="nouser" eventKey="nouser">
                            <div className="d-flex align-items-center">
                                <Avatar icon="fa-light fa-user" size="sm" invert />
                                <div className="text-truncate" style={{maxWidth: 'calc(100% - 30px)'}}>
                                    Not Assigned
                                </div>
                            </div>
                        </Dropdown.Item>
                    )}
                    
                    {/* Extra options provided by parent component */}
                    {extraOptions.map(option => (
                        <Dropdown.Item key={option.id} eventKey={option.id}>
                            <div className="d-flex align-items-center">
                                <Avatar icon={option.icon ? option.icon : undefined} initials={!option.icon ? option.label.charAt(0) : undefined} size="sm" />
                                <div className="text-truncate" style={{maxWidth: 'calc(100% - 30px)'}}>
                                    {option.label}
                                </div>
                            </div>
                        </Dropdown.Item>
                    ))}
                
                    {(extraOptions.length > 0 || showNotAssigned) && <Dropdown.Divider />}
                
                    {(users.length > 6 ? filteredUsers : users).map((user) => {
                        const userId = getUserId(user);
                        if (!userId) return null;
                        
                        const initials = getInitials(user.Name || "");
                        const color = getUserColor(userId, currentUserId);
                        
                        return (
                            <Dropdown.Item
                                key={userId}
                                eventKey={userId}
                            >
                                <div className="d-flex align-items-center">
                                    <Avatar initials={initials} size="sm" color={color} />

                                    <div className="text-truncate" style={{maxWidth: 'calc(100% - 30px)'}}>
                                        {user.Name}
                                        {user.LSUCID && <i className="fa-regular fa-scale-balanced ms-2"></i>}
                                    </div>
                                </div>
                            </Dropdown.Item>
                        );
                    })}
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default SearchableUserDropdown;
